// ── Planar face enumeration engine for the Lacuna board ──

import type { BoardState, Vertex, Strand, PlayerColor } from '../game/state';
import {
  anchorAngle,
  anchorIndex,
  nextAnchorCW,
  normalizeAngle,
  arcDepartureAngle,
  angleTo,
  BOARD_RADIUS,
  ANCHOR_COUNT,
  pointInPolygon,
  arcPolyline,
  chaikinSmooth,
} from '../board/geometry';

// ── Exported types ──

export interface HalfEdge {
  from: string;
  to: string;
  strandId: string | null; // null for arc edges
  isArc: boolean;
  color: PlayerColor | null; // null for arcs
}

export interface Face {
  id: number;
  edges: HalfEdge[];
  isInterior: boolean;
  isExterior: boolean;
  isMonochrome: boolean;
  monochromeColor: PlayerColor | null;
  boundaryLength: number;
  vertices: string[]; // unique vertex IDs on this face's boundary
}

// ── Internal types ──

interface InternalHalfEdge {
  from: string;
  to: string;
  strandId: string | null;
  isArc: boolean;
  color: PlayerColor | null;
  departureAngle: number; // angle leaving `from` toward `to`
}

/**
 * Build adjacency with sorted neighbor lists and enumerate all faces
 * of the planar graph formed by the board's anchors, arc edges, and strands.
 */
export function enumerateFaces(board: BoardState): Face[] {
  // ── Step 1: Build half-edges ──

  const halfEdges: InternalHalfEdge[] = [];

  // Arc edges between consecutive anchors (12 edges around the circle)
  for (let i = 1; i <= ANCHOR_COUNT; i++) {
    const j = nextAnchorCW(i);
    const fromId = `A${i}`;
    const toId = `A${j}`;

    halfEdges.push({
      from: fromId,
      to: toId,
      strandId: null,
      isArc: true,
      color: null,
      departureAngle: arcDepartureAngle(i, j),
    });
    halfEdges.push({
      from: toId,
      to: fromId,
      strandId: null,
      isArc: true,
      color: null,
      departureAngle: arcDepartureAngle(j, i),
    });
  }

  // Strand edges
  for (const strand of Object.values(board.strands ?? {})) {
    const fromV = board.vertices[strand.from];
    const toV = board.vertices[strand.to];
    if (!fromV || !toV) continue;

    // Use actual waypoint direction at each endpoint so the planar embedding
    // reflects the drawn curve, not just the straight line between endpoints.
    let angleForward: number;
    let angleBackward: number;
    const wp = strand.waypoints;
    if (wp && wp.length >= 2) {
      angleForward = normalizeAngle(angleTo(fromV, wp[1]));
      angleBackward = normalizeAngle(angleTo(toV, wp[wp.length - 2]));
    } else {
      angleForward = normalizeAngle(angleTo(fromV, toV));
      angleBackward = normalizeAngle(angleTo(toV, fromV));
    }

    halfEdges.push({
      from: strand.from,
      to: strand.to,
      strandId: strand.id,
      isArc: false,
      color: strand.color,
      departureAngle: angleForward,
    });
    halfEdges.push({
      from: strand.to,
      to: strand.from,
      strandId: strand.id,
      isArc: false,
      color: strand.color,
      departureAngle: angleBackward,
    });
  }

  // ── Step 2: Build adjacency — at each vertex, neighbors sorted by departure angle DECREASING (clockwise) ──

  // Group half-edges by source vertex
  const outgoing = new Map<string, InternalHalfEdge[]>();
  for (const he of halfEdges) {
    let list = outgoing.get(he.from);
    if (!list) {
      list = [];
      outgoing.set(he.from, list);
    }
    list.push(he);
  }

  // Sort each vertex's outgoing edges by departure angle DECREASING
  for (const [, list] of outgoing) {
    list.sort((a, b) => b.departureAngle - a.departureAngle);
  }

  // ── Step 3: Build a map for fast "next half-edge" lookup ──
  // Key: "from->to" (with strandId/arc discriminator for multi-edges)
  // We need to handle the case where two vertices might be connected by
  // both an arc and a strand, or multiple strands. Use a composite key.

  function halfEdgeKey(he: InternalHalfEdge): string {
    if (he.isArc) return `arc:${he.from}->${he.to}`;
    return `strand:${he.strandId}:${he.from}->${he.to}`;
  }

  // For finding the twin: the reverse half-edge
  function twinKey(he: InternalHalfEdge): string {
    if (he.isArc) return `arc:${he.to}->${he.from}`;
    return `strand:${he.strandId}:${he.to}->${he.from}`;
  }

  const halfEdgeMap = new Map<string, InternalHalfEdge>();
  for (const he of halfEdges) {
    halfEdgeMap.set(halfEdgeKey(he), he);
  }

  // Next half-edge in face traversal:
  // For half-edge u→v, go to vertex v, find the incoming direction (from u),
  // look for that edge's twin (v→u) in v's sorted list, then take the next one clockwise.
  function nextHalfEdge(he: InternalHalfEdge): InternalHalfEdge {
    const vList = outgoing.get(he.to);
    if (!vList || vList.length === 0) {
      throw new Error(`No outgoing edges from vertex ${he.to}`);
    }

    // Find the reverse half-edge (v→u) in v's outgoing list
    // We need to match the specific edge (arc vs strand, strandId)
    const reverseKey = twinKey(he);
    let idx = -1;
    for (let i = 0; i < vList.length; i++) {
      if (halfEdgeKey(vList[i]) === reverseKey) {
        idx = i;
        break;
      }
    }

    if (idx === -1) {
      throw new Error(
        `Could not find reverse of half-edge ${halfEdgeKey(he)} in vertex ${he.to}'s neighbor list`,
      );
    }

    // Next in clockwise order: since sorted decreasing, the "next clockwise"
    // after index i is index (i+1) % length
    const nextIdx = (idx + 1) % vList.length;
    return vList[nextIdx];
  }

  // ── Step 4: Enumerate faces by walking half-edge chains ──

  const visited = new Set<string>();
  const faces: Face[] = [];
  let faceId = 0;

  for (const he of halfEdges) {
    const key = halfEdgeKey(he);
    if (visited.has(key)) continue;

    // Walk the face
    const faceEdges: InternalHalfEdge[] = [];
    let current = he;
    let safety = 0;
    const maxSteps = halfEdges.length + 1;

    do {
      const currentKey = halfEdgeKey(current);
      if (visited.has(currentKey)) break;
      visited.add(currentKey);
      faceEdges.push(current);
      current = nextHalfEdge(current);
      safety++;
      if (safety > maxSteps) {
        throw new Error('Face traversal exceeded maximum steps — possible infinite loop');
      }
    } while (halfEdgeKey(current) !== halfEdgeKey(he));

    if (faceEdges.length === 0) continue;

    // Convert to exported HalfEdge format
    const exportedEdges: HalfEdge[] = faceEdges.map((e) => ({
      from: e.from,
      to: e.to,
      strandId: e.strandId,
      isArc: e.isArc,
      color: e.color,
    }));

    // Unique vertices
    const vertexSet = new Set<string>();
    for (const e of faceEdges) {
      vertexSet.add(e.from);
    }
    const vertices = Array.from(vertexSet);

    // Count arc edges
    const arcCount = faceEdges.filter((e) => e.isArc).length;
    const isInterior = arcCount === 0;

    // Strand half-edges (boundary length)
    const strandEdges = faceEdges.filter((e) => !e.isArc);
    const boundaryLength = strandEdges.length;

    // Monochrome check: all strand edges same color
    let isMonochrome = false;
    let monochromeColor: PlayerColor | null = null;
    if (strandEdges.length > 0) {
      const colors = new Set(strandEdges.map((e) => e.color));
      if (colors.size === 1) {
        isMonochrome = true;
        monochromeColor = strandEdges[0].color;
      }
    }

    faces.push({
      id: faceId++,
      edges: exportedEdges,
      isInterior,
      isExterior: false, // will be set below
      isMonochrome,
      monochromeColor,
      boundaryLength,
      vertices,
    });
  }

  // ── Step 5: Identify and mark the exterior face ──

  const exteriorPoint = { x: 0, y: BOARD_RADIUS + 1 };
  for (const face of faces) {
    const polygon = buildFacePolygon(face, board);
    if (pointInPolygon(exteriorPoint.x, exteriorPoint.y, polygon)) {
      face.isExterior = true;
      break;
    }
  }

  // If no face was found to contain the exterior point, the largest face with
  // all arc edges on the boundary is likely the exterior. This is a fallback.
  if (!faces.some((f) => f.isExterior)) {
    // The exterior face should contain all 12 arc edges (24 half-edges for arcs,
    // but the exterior face only sees 12 of them — the outward-facing ones).
    // As a fallback, pick the face with the most arc edges.
    let maxArcs = -1;
    let extFace: Face | null = null;
    for (const face of faces) {
      const arcs = face.edges.filter((e) => e.isArc).length;
      if (arcs > maxArcs) {
        maxArcs = arcs;
        extFace = face;
      }
    }
    if (extFace) {
      extFace.isExterior = true;
    }
  }

  return faces;
}

/**
 * Build a polygon (array of {x,y} points) for a face, suitable for
 * point-in-polygon testing. For strand edges, use straight lines.
 * For arc edges, use arcPolyline to approximate the arc.
 */
function buildFacePolygon(
  face: Face,
  board: BoardState,
): Array<{ x: number; y: number }> {
  const points: Array<{ x: number; y: number }> = [];

  for (const edge of face.edges) {
    if (edge.isArc) {
      const fromIdx = anchorIndex(edge.from);
      const toIdx = anchorIndex(edge.to);
      if (fromIdx > 0 && toIdx > 0) {
        const arcPts = arcPolyline(anchorAngle(fromIdx), anchorAngle(toIdx));
        // Add all points except the last (to avoid duplication with next edge's start)
        for (let i = 0; i < arcPts.length - 1; i++) {
          points.push(arcPts[i]);
        }
      }
    } else {
      // Strand edge: follow Chaikin-smoothed waypoints so the polygon
      // matches the visually rendered curve for point-in-polygon testing.
      const strand = edge.strandId ? (board.strands ?? {})[edge.strandId] : null;
      if (strand?.waypoints && strand.waypoints.length >= 2) {
        const forward = edge.from === strand.from;
        const raw = forward ? strand.waypoints : [...strand.waypoints].reverse();
        const smoothed = chaikinSmooth(raw, 2);
        // Add all points except the last (next edge's start provides it)
        for (let i = 0; i < smoothed.length - 1; i++) {
          points.push({ x: smoothed[i].x, y: smoothed[i].y });
        }
      } else {
        const fromV = board.vertices[edge.from];
        if (fromV) {
          points.push({ x: fromV.x, y: fromV.y });
        }
      }
    }
  }

  return points;
}

/**
 * Find which face contains a given point (for node placement).
 * Returns null if the point is not inside any non-exterior face.
 */
export function findFaceAtPoint(
  x: number,
  y: number,
  faces: Face[],
  board: BoardState,
): Face | null {
  for (const face of faces) {
    if (face.isExterior) continue;
    const polygon = buildFacePolygon(face, board);
    if (polygon.length < 3) continue;
    if (pointInPolygon(x, y, polygon)) {
      return face;
    }
  }
  return null;
}

/**
 * Get the unique vertex IDs on the boundary of a face.
 */
export function getBoundaryVertices(face: Face): string[] {
  return face.vertices;
}

/**
 * A face is a lacuna if it is interior (no arc edges), not exterior,
 * monochrome (all strand edges are the same color), and has at least
 * 3 boundary strand edges.
 */
export function isLacuna(face: Face): boolean {
  return (
    face.isInterior &&
    !face.isExterior &&
    face.isMonochrome &&
    face.monochromeColor !== null &&
    face.boundaryLength >= 3
  );
}

/**
 * Find all faces that qualify as new (unsealed) lacunae.
 */
export function findNewLacunae(faces: Face[]): Face[] {
  return faces.filter(isLacuna);
}

/**
 * Get all non-exterior faces that have not been sealed.
 */
export function getLiveFaces(faces: Face[], sealedFaceIds: Set<number>): Face[] {
  return faces.filter((f) => !f.isExterior && !sealedFaceIds.has(f.id));
}
